/**
 * Ops API service for deployment management.
 * Requires Cognito authentication with admin group membership.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// Remove trailing slash to avoid double-slash in URLs
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export interface LastDeployment {
  timestamp: string;
  status: string;
  commit_sha: string;
  triggered_by: string;
  github_run_url?: string;
}

export interface DeploymentInfo {
  deployment_id: string;
  label: string;
  region: string;
  domain: string;
  environment: string;
  status: 'operational' | 'degraded' | 'outage' | 'unknown';
  commit_sha?: string;
  last_checked?: string;
  health_endpoint?: string;
  last_deployment?: LastDeployment | null;
}

export interface DeploymentHistoryItem {
  deployment_id: string;
  timestamp: string;
  commit_sha?: string;
  status: 'triggered' | 'pending' | 'in_progress' | 'success' | 'failure' | 'failed_to_trigger' | 'cancelled';
  triggered_by: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  github_run_id?: string;
  github_run_url?: string;
  head_branch?: string;
  event?: string;
}

export interface WorkflowRun {
  id: number;
  deployment_id: string | null;
  repo: string;
  name: string;
  display_title: string;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | null;
  head_sha: string;
  head_branch: string;
  event: string;
  actor: {
    login: string;
    avatar_url: string;
  };
  triggering_actor: {
    login: string;
    avatar_url: string;
  };
  created_at: string;
  updated_at: string;
  run_started_at: string;
  html_url: string;
  jobs_url: string;
}

export interface GitHubCommit {
  sha: string;
  short_sha: string;
  message: string;
  full_message: string;
  author: {
    name: string;
    email: string;
    date: string;
    login?: string;
    avatar_url?: string;
  };
  committer: {
    name: string;
    date: string;
  };
  html_url: string;
  deployed_to: string[];
  stats?: {
    additions: number;
    deletions: number;
    total: number;
  };
}

export interface CommitComparison {
  base: string;
  head: string;
  status: 'ahead' | 'behind' | 'identical' | 'diverged';
  ahead_by: number;
  behind_by: number;
  total_commits: number;
  commits: {
    sha: string;
    short_sha: string;
    message: string;
    author: {
      name: string;
      login?: string;
      avatar_url?: string;
      date: string;
    };
    html_url: string;
  }[];
  html_url: string;
  diff_url: string;
  files_changed: number;
}

export interface ChangelogEntry {
  sha: string;
  short_sha: string;
  title: string;
  body: string;
  category: string;
  author: {
    name: string;
    login?: string;
    avatar_url?: string;
  };
  date: string;
  html_url: string;
}

export interface Changelog {
  base: string;
  head: string;
  total_commits: number;
  entries: ChangelogEntry[];
  by_category: Record<string, ChangelogEntry[]>;
  files_changed: number;
  additions: number;
  deletions: number;
  html_url: string;
  diff_url: string;
}

export interface RollbackResult {
  message: string;
  deployment_id: string;
  rollback_to: string;
  previous_deployment: {
    commit_sha: string;
    timestamp: string;
    deployed_by: string;
  };
  status: string;
}

export interface WorkflowStep {
  number: number;
  name: string;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | 'skipped' | null;
  started_at?: string;
  completed_at?: string;
}

export interface WorkflowJob {
  id: number;
  name: string;
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | null;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  html_url: string;
  steps: WorkflowStep[];
  runner_name?: string;
  runner_group_name?: string;
}

export interface WorkflowJobsResponse {
  run_id: string;
  total_jobs: number;
  jobs: WorkflowJob[];
}

export interface WorkflowLogsResponse {
  run_id: string;
  job_id?: string;
  logs?: string;
  logs_by_job?: Record<string, string>;
  format: 'text' | 'zip';
}

export interface IncidentCreateRequest {
  title: string;
  severity: 'minor' | 'major' | 'critical';
  message: string;
  affected_components: string[];
}

export interface IncidentUpdateRequest {
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  message: string;
}

/**
 * Get auth token from Amplify session.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    // Use ID token for API Gateway Cognito authorizer
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Make authenticated API request.
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = token;
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Fetch all deployments with their current status.
 */
export async function fetchDeployments(): Promise<DeploymentInfo[]> {
  const response = await authFetch(`${API_BASE}/api/ops/deployments`);

  if (!response.ok) {
    throw new Error(`Failed to fetch deployments: ${response.status}`);
  }

  const data = await response.json();
  return data.deployments || [];
}

/**
 * Fetch deployment history for a specific deployment.
 */
export async function fetchDeploymentHistory(
  deploymentId: string,
  limit: number = 20
): Promise<DeploymentHistoryItem[]> {
  const response = await authFetch(
    `${API_BASE}/api/ops/deployments/${deploymentId}/history?limit=${limit}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch deployment history: ${response.status}`);
  }

  const data = await response.json();
  return data.history || [];
}

/**
 * Trigger a new deployment.
 */
export async function triggerDeploy(
  deploymentId: string,
  commit?: string,
  ref?: string
): Promise<{ message: string; deployment_id: string; status: string }> {
  const response = await authFetch(`${API_BASE}/api/ops/deployments/${deploymentId}/deploy`, {
    method: 'POST',
    body: JSON.stringify({ commit, ref: ref || 'main' }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to trigger deployment: ${response.status}`);
  }

  return response.json();
}

/**
 * Sync deployment history from GitHub for a specific deployment.
 */
export async function syncDeploymentHistory(
  deploymentId: string
): Promise<{ synced: number; total_runs: number }> {
  const response = await authFetch(`${API_BASE}/api/ops/deployments/${deploymentId}/sync`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`Failed to sync deployment history: ${response.status}`);
  }

  return response.json();
}

/**
 * Sync all deployments from GitHub.
 */
export async function syncAllDeployments(): Promise<{
  message: string;
  results: Record<string, { synced: number; total_runs: number }>;
}> {
  const response = await authFetch(`${API_BASE}/api/ops/github/runs`, {
    method: 'POST',
  });

  // Fallback to individual syncs if bulk endpoint doesn't work
  if (!response.ok) {
    // Try syncing each deployment individually
    const deployments = await fetchDeployments();
    const results: Record<string, { synced: number; total_runs: number }> = {};

    for (const deployment of deployments) {
      try {
        const result = await syncDeploymentHistory(deployment.deployment_id);
        results[deployment.deployment_id] = result;
      } catch {
        results[deployment.deployment_id] = { synced: 0, total_runs: 0 };
      }
    }

    return { message: 'Synced all deployments', results };
  }

  return response.json();
}

/**
 * Fetch current workflow runs (including in-progress).
 */
export async function fetchWorkflowRuns(
  status?: 'in_progress' | 'completed' | 'queued'
): Promise<{ runs: WorkflowRun[]; total: number; in_progress_count: number }> {
  const params = status ? `?status=${status}` : '';
  const response = await authFetch(`${API_BASE}/api/ops/github/runs${params}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch workflow runs: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch recent commits with details.
 */
export async function fetchCommits(options?: {
  branch?: string;
  perPage?: number;
  since?: string;
}): Promise<{ commits: GitHubCommit[]; repo: string; branch: string; total: number }> {
  const params = new URLSearchParams();
  if (options?.branch) params.set('branch', options.branch);
  if (options?.perPage) params.set('per_page', options.perPage.toString());
  if (options?.since) params.set('since', options.since);

  const queryString = params.toString();
  const url = `${API_BASE}/api/ops/github/commits${queryString ? `?${queryString}` : ''}`;

  const response = await authFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch commits: ${response.status}`);
  }

  return response.json();
}

/**
 * Compare commits between two SHAs or deployments.
 */
export async function compareCommits(
  base: string,
  head: string = 'main'
): Promise<CommitComparison> {
  const params = new URLSearchParams({ base, head });
  const response = await authFetch(`${API_BASE}/api/ops/github/compare?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to compare commits: ${response.status}`);
  }

  return response.json();
}

/**
 * Create a new incident.
 */
export async function createIncident(incident: IncidentCreateRequest): Promise<{ incident_id: string }> {
  const response = await authFetch(`${API_BASE}/api/ops/incidents`, {
    method: 'POST',
    body: JSON.stringify(incident),
  });

  if (!response.ok) {
    throw new Error(`Failed to create incident: ${response.status}`);
  }

  return response.json();
}

/**
 * Update an existing incident.
 */
export async function updateIncident(
  incidentId: string,
  update: IncidentUpdateRequest
): Promise<void> {
  const response = await authFetch(`${API_BASE}/api/ops/incidents/${incidentId}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  });

  if (!response.ok) {
    throw new Error(`Failed to update incident: ${response.status}`);
  }
}

/**
 * Fetch all active incidents for ops management.
 */
export async function fetchActiveIncidents(): Promise<{
  incident_id: string;
  title: string;
  status: string;
  severity: string;
  created_at: string;
  affected_components: string[];
}[]> {
  const response = await authFetch(`${API_BASE}/api/ops/incidents?status=active`);

  if (!response.ok) {
    throw new Error(`Failed to fetch incidents: ${response.status}`);
  }

  const data = await response.json();
  return data.incidents || [];
}

/**
 * Resolve an incident.
 */
export async function resolveIncident(incidentId: string, message: string): Promise<void> {
  await updateIncident(incidentId, {
    status: 'resolved',
    message,
  });
}

/**
 * Trigger a rollback to the previous successful deployment.
 */
export async function triggerRollback(deploymentId: string): Promise<RollbackResult> {
  const response = await authFetch(`${API_BASE}/api/ops/deployments/${deploymentId}/rollback`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to trigger rollback: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch changelog between two commits or deployments.
 */
export async function fetchChangelog(
  base: string,
  head: string = 'main'
): Promise<Changelog> {
  const params = new URLSearchParams({ base, head });
  const response = await authFetch(`${API_BASE}/api/ops/github/changelog?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch changelog: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch workflow jobs and steps for a specific run.
 */
export async function fetchWorkflowJobs(runId: number | string): Promise<WorkflowJobsResponse> {
  const response = await authFetch(`${API_BASE}/api/ops/github/runs/${runId}/jobs`);

  if (!response.ok) {
    throw new Error(`Failed to fetch workflow jobs: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch logs for a workflow run or specific job.
 */
export async function fetchWorkflowLogs(
  runId: number | string,
  jobId?: number | string
): Promise<WorkflowLogsResponse> {
  const params = jobId ? `?job_id=${jobId}` : '';
  const response = await authFetch(`${API_BASE}/api/ops/github/runs/${runId}/logs${params}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch workflow logs: ${response.status}`);
  }

  return response.json();
}

export interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
    url: string;
  };
  protected: boolean;
}

export interface ConfigResponse {
  admin_group_name: string;
}

/**
 * Fetch configuration information (e.g., admin group name).
 * MED-044 FIX: Fetch admin group name from API instead of hardcoding in frontend.
 */
export async function fetchConfig(): Promise<ConfigResponse> {
  const response = await authFetch(`${API_BASE}/api/ops/config`);

  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch branches from the repository.
 */
export async function fetchBranches(): Promise<{ branches: GitHubBranch[] }> {
  const response = await authFetch(`${API_BASE}/api/ops/github/branches`);

  if (!response.ok) {
    throw new Error(`Failed to fetch branches: ${response.status}`);
  }

  return response.json();
}
